import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import styles from "./LegalPage.module.css";
import TranslateToggle from "../components/TranslateToggle";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { sanitizeHtml } from "../utils/sanitizeHtml";

/**
 * LegalPage component to display dynamic content for Privacy Policy and Terms of Service.
 * Fetches data from Firestore 'legal' collection.
 */
function TranslateToggleWithDisclaimer({ text }) {
  const { t } = useTranslation();
  const [translated, setTranslated] = useState(false);
  return (
    <>
      <TranslateToggle
        text={text}
        className={styles.translateToggle}
        onTranslated={() => setTranslated(true)}
      />
      {translated && (
        <div className={styles.translationDisclaimer}>
          <small>
            {t('translation.disclaimer', 'Disclaimer: This translation is provided for convenience only. The original legal text prevails in case of discrepancies.')}
          </small>
        </div>
      )}
    </>
  );
}

const LegalPage = ({ forcedType }) => {
  const { type: paramType } = useParams(); // Expected values: 'privacy' or 'terms'
  const type = forcedType || paramType;
  const { t } = useTranslation();
  const [legalData, setLegalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchLegalContent = async () => {
      setLoading(true);
      setError("");
      try {
        // Ensure we only look for valid legal document IDs
        const docId = type === "privacy" ? "privacy" : "terms";
        const docRef = doc(db, "legal", docId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setLegalData(docSnap.data());
        } else {
          setError(t('common.status.notFound', 'Legal document not found.'));
        }
      } catch (err) {
        console.error("Error fetching legal content:", err);
        setError(t('common.error', 'An error occurred while loading the content.'));
      } finally {
        setLoading(false);
      }
    };
    if (type) {
      fetchLegalContent();
    }
  }, [type, t]);

  const title = type === 'privacy' 
    ? t('common.privacyPolicy', 'Privacy Policy') 
    : t('common.termsOfService', 'Terms of Service');

  const safeLegalHtml = useMemo(() => sanitizeHtml(legalData?.content || ''), [legalData?.content]);

  return (
    <div className={styles.legalPage}>
      <Helmet>
        <title>{title} | {t('common.siteName', 'PeaksOnFoot')}</title>
      </Helmet>
      
      <div className={styles.contentSection}>
        {loading ? (
          <div className={styles.loading}>{t('common.loading', 'Loading...')}</div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>{error}</p>
            <Link to="/" className={styles.homeLink}>{t('action.goHome', 'Back to Home')}</Link>
          </div>
        ) : (
          <article className={styles.article}>
            <h1 className={styles.pageTitle}>{title}</h1>
            <div className={styles.htmlContent}>
              <div dangerouslySetInnerHTML={{ __html: safeLegalHtml }} />
              <TranslateToggleWithDisclaimer text={safeLegalHtml} />
            </div>
            {legalData?.updatedAt && (
              <footer className={styles.legalFooter}>
                <p>
                  {t('common.lastUpdated', 'Last updated')}: {
                    legalData.updatedAt?.seconds 
                      ? new Date(legalData.updatedAt.seconds * 1000).toLocaleDateString()
                      : t('common.unknownDate', 'Recently')
                  }
                </p>
              </footer>
            )}
          </article>
        )}
      </div>
      
      <div className={styles.navigation}>
        <Link to="/" className={styles.backButton}>
          &larr; {t('action.goHome', 'Back to Home')}
        </Link>
      </div>
    </div>
  );
};

export default LegalPage;
